/**
 * Reading a `text/event-stream` response body.
 *
 * Written once and shared, because the two things that go wrong here go wrong
 * identically for every caller:
 *
 * 1. A CHUNK IS NOT A MESSAGE. The network splits wherever it likes, so an event
 *    can arrive in three reads and two events can arrive in one. Anything that
 *    parses per chunk works on localhost and drops tokens over a real connection.
 * 2. The terminator is a BLANK LINE, and servers disagree about `\r\n`. Splitting
 *    on `\n\n` alone silently swallows every event from the ones that send CRLF.
 *
 * `fetch` with a manual reader rather than `EventSource`, for two reasons that
 * both rule EventSource out: it cannot send a POST body, and it cannot carry an
 * Authorization header. Reading the body ourselves also gives an abort that
 * actually stops the request.
 */
export interface SseEvent {
	readonly event: string;
	readonly data: string;
}

export async function readEventStream(
	body: ReadableStream<Uint8Array>,
	onEvent: (event: SseEvent) => void,
	signal?: AbortSignal,
): Promise<void> {
	const reader = body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";

	try {
		while (true) {
			const { done, value } = await reader.read();
			if (done) break;

			// `stream: true` matters: a multibyte character split across two chunks
			// decodes to U+FFFD otherwise, and reaches the reader as a broken glyph
			// in the middle of a word.
			buffer += decoder.decode(value, { stream: true });
			buffer = buffer.replace(/\r\n/g, "\n");

			let boundary = buffer.indexOf("\n\n");
			while (boundary >= 0) {
				const parsed = parseBlock(buffer.slice(0, boundary));
				if (parsed) onEvent(parsed);
				buffer = buffer.slice(boundary + 2);
				boundary = buffer.indexOf("\n\n");
			}
		}

		// A stream that ends without a trailing blank line still owes us its last
		// event — servers do this on the terminal message often enough to matter.
		const tail = parseBlock(buffer);
		if (tail) onEvent(tail);
	} catch (error) {
		// An abort is the person interrupting, not a failure to report.
		if (signal?.aborted) return;
		throw error;
	} finally {
		// An un-released reader keeps the socket open until GC, which under load is
		// a leak with no error attached to it.
		reader.releaseLock();
	}
}

function parseBlock(raw: string): SseEvent | null {
	let event = "message";
	const data: string[] = [];

	for (const line of raw.split("\n")) {
		if (line === "" || line.startsWith(":")) continue;
		if (line.startsWith("event:")) event = line.slice(6).trim();
		// Multiple `data:` lines in one event join with a newline. That is the
		// spec, and it is how a server sends a JSON body that contains one.
		else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
	}

	if (data.length === 0) return null;
	return { event, data: data.join("\n") };
}

/** Parses an event payload, or null. One malformed frame must not end a turn. */
export function parseEventData<T>(data: string): T | null {
	try {
		return JSON.parse(data) as T;
	} catch {
		return null;
	}
}
